import styled from "styled-components"
import { Link } from "react-router-dom"
import logo from "../assets/peplogo.png"

const policySections = [
  {
    title: "Location Data",
    description:
      "Pepchat uses location only to help users discover nearby activities, events, people, vibes, and plans. Location may be manually entered by the user or accessed through device permission. Pepchat does not sell location data, does not track users across apps, and does not use hidden background tracking.",
  },
  {
    title: "Camera Access",
    description:
      "Pepchat uses camera access only when users choose to capture Moments, upload profile photos, or share images inside the app. Pepchat does not access the camera in the background.",
  },
  {
    title: "Microphone Access",
    description:
      "Pepchat uses microphone access only for audio chat, voice rooms, and real-time voice communication. Pepchat does not access or record microphone audio in the background.",
  },
  {
    title: "Chats and Messages",
    description:
      "Pepchat supports private and group chats. Private chats are protected with encryption, and where supported, end-to-end encryption is used. Pepchat does not sell chat data and does not use private chats for advertising.",
  },
  {
    title: "Data Usage",
    description:
      "Pepchat uses user data only to operate the app, improve safety, personalize discovery, show nearby activities, and support communication features.",
  },
  {
    title: "Data Sharing",
    description:
      "Pepchat does not sell personal data. Data may only be shared when required for app operations, legal compliance, safety, fraud prevention, or trusted service providers.",
  },
  {
    title: "User Controls",
    description:
      "Users can update profile details, manage app permissions, delete content, and request account deletion.",
  },
]

const Privacy = () => {
  return (
    <Page>
      <Header>
        <HeaderInner>
          <LogoLockup to="/">
            <LogoMark src={logo} alt="Pepchat logo" />
            <LogoText>
              <strong>Pepchat</strong>
              <span>Personal engagement platform</span>
            </LogoText>
          </LogoLockup>

          <BackLink to="/">Back to Pepchat</BackLink>
        </HeaderInner>
      </Header>

      <Main>
        <DetailsSection>
          <DetailsIntro>
            <Eyebrow>Privacy at Pepchat</Eyebrow>
            <DetailsTitle>Privacy Policy</DetailsTitle>
            <DetailsText>
              Clear information about how Pepchat uses data and permissions to provide its features.
            </DetailsText>
          </DetailsIntro>

          <PolicyGrid>
            {policySections.map((section, index) => (
              <PolicyCard key={section.title}>
                <PolicyNumber>{String(index + 1).padStart(2, "0")}</PolicyNumber>
                <h3>{section.title}</h3>
                <p>{section.description}</p>
              </PolicyCard>
            ))}
          </PolicyGrid>
        </DetailsSection>
      </Main>

      <Footer>
        <span>© 2026 Pepchat.</span>
        <Link to="/">Give people a reason to connect.</Link>
      </Footer>
    </Page>
  )
}

export default Privacy

const Page = styled.div`
  min-height: 100vh;
  width: 100%;
  overflow-x: hidden;
  background: #000000;
  color: #ffffff;
  font-family: "Manrope", "Segoe UI", sans-serif;
`

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  width: 100%;
  background: rgba(0, 0, 0, 0.92);
  border-bottom: 1px solid #242424;
  backdrop-filter: blur(18px);
`

const HeaderInner = styled.div`
  width: min(1180px, calc(100% - 40px));
  min-height: 78px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: 560px) {
    width: min(100%, calc(100% - 24px));
  }
`

const LogoLockup = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: inherit;
  text-decoration: none;
`

const LogoMark = styled.img`
  width: 44px;
  height: 44px;
  flex: 0 0 44px;
  border: 1px solid #303030;
  border-radius: 10px;
  display: block;
`

const LogoText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;

  strong {
    font-family: "Space Grotesk", "Manrope", sans-serif;
    font-size: 1.06rem;
    line-height: 1;
  }

  span {
    color: #a7a7a7;
    font-size: 0.84rem;
  }

  @media (max-width: 420px) {
    span {
      display: none;
    }
  }
`

const BackLink = styled(Link)`
  border: 1px solid #ffffff;
  background: #ffffff;
  color: #000000;
  min-height: 44px;
  padding: 0 18px;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 800;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: #000000;
    color: #ffffff;
    transform: translateY(-1px);
  }

  @media (max-width: 420px) {
    padding: 0 12px;
    font-size: 0.78rem;
  }
`

const Main = styled.main`
  width: 100%;
`

const Eyebrow = styled.span`
  color: #b5b5b5;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.4;
  text-transform: uppercase;
`

const DetailsSection = styled.section`
  width: min(900px, calc(100% - 40px));
  margin: 0 auto;
  padding: 86px 0 112px;

  @media (max-width: 720px) {
    width: min(100%, calc(100% - 24px));
    padding: 64px 0 78px;
  }
`

const DetailsIntro = styled.div`
  max-width: 720px;
  margin-bottom: 48px;
`

const DetailsTitle = styled.h2`
  margin: 16px 0;
  color: #ffffff;
  font-family: "Space Grotesk", "Manrope", sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  line-height: 0.95;

  @media (max-width: 560px) {
    font-size: 2.6rem;
  }
`

const DetailsText = styled.p`
  margin: 0;
  color: #c9c9c9;
  font-size: 1.1rem;
  line-height: 1.7;
`

const PolicyGrid = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #303030;
  border-left: 1px solid #303030;
`

const PolicyCard = styled.article`
  padding: clamp(24px, 4vw, 38px);
  border-right: 1px solid #303030;
  border-bottom: 1px solid #303030;
  background: #050505;

  h3 {
    margin: 24px 0 14px;
    color: #ffffff;
    font-family: "Space Grotesk", "Manrope", sans-serif;
    font-size: 1.55rem;
    line-height: 1.1;
  }

  p {
    margin: 0;
    color: #bdbdbd;
    line-height: 1.75;
  }

  &:hover {
    background: #090909;
  }
`

const PolicyNumber = styled.span`
  color: #858585;
  font-size: 0.8rem;
  font-weight: 800;
`

const Footer = styled.footer`
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
  padding: 28px 0;
  border-top: 1px solid #242424;
  display: flex;
  justify-content: space-between;
  gap: 20px;
  color: #8f8f8f;
  font-size: 0.82rem;

  a {
    color: #d6d6d6;
    text-decoration: none;
  }

  @media (max-width: 560px) {
    width: min(100%, calc(100% - 24px));
    flex-direction: column;
  }
`
